import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Link, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Select, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text } from '@fluentui/react-components';
import { INC_CATEGORIES, INC_CATEGORY_BY_KEY, INC_PRIORITIES, INC_PRIORITY_LABELS, INC_STATUSES, INC_STATUS_CUSTOMER_LABELS, INC_STATUS_LABELS } from '@tvmf/shared';
import { api } from '../api';
import { useAuth } from '../auth';
import { DataTable } from '../components/DataTable';
import { IncidentNotices, PastNoticesDialog } from '../components/IncidentNotices';
import { KnowledgeDialog } from '../components/IncidentKnowledge';
import { IncidentReportsDialog } from '../components/IncidentReports';
import { IncidentSettingsDialog } from '../components/IncidentSettings';
import { IncQueueTiles, IncSlaBadge, IncStatusBadge, MajorBadge, MajorClockBadge, PriorityBadge, incQueueFilter, type IncQueueFilter, type IncidentRow } from '../components/IncidentBits';
import { Page } from '../components/Page';
import { EmptyState, TableSkeleton } from '../components/states';
import { LoadError, NoTenant } from '../components/records';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { useDebounced } from '../hooks/useDebounced';
import { useNotify } from '../hooks/useNotify';
import { useSort } from '../lib/sort';
import { useSrStyles } from './ServiceRequests';

/**
 * Incidents: something that worked has stopped working, or is working badly.
 * Customers raise them and follow progress; the engineers and admins on the
 * customer work them. See docs/INCIDENT-MANAGEMENT.md.
 */
export function Incidents() {
  const { activeTenantId, can, me } = useAuth();
  const cs = useSrStyles();
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [assigned, setAssigned] = useState('');
  const [qInput, setQInput] = useState('');
  const q = useDebounced(qInput);
  const [tile, setTile] = useState<IncQueueFilter>('all');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const staff = can('inc:manage');
  const qc = useQueryClient();
  const sandbox = me?.tenants.find((t) => t.id === activeTenantId)?.sandbox ?? false;
  const notify = useNotify();
  const demo = useMutation({
    mutationFn: () => api(`/t/${activeTenantId}/incidents/demo`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incidents'] });
      notify('Six demo incidents added');
    },
    onError: (e) => notify('Could not add demo incidents', { intent: 'error', body: e instanceof Error ? e.message : undefined }),
  });

  const enabled = me?.tenants.find((t) => t.id === activeTenantId)?.managedServices ?? false;
  const base = `/t/${activeTenantId}/incidents`;
  const params = new URLSearchParams({ status });
  if (category) params.set('category', category);
  if (priority) params.set('priority', priority);
  if (assigned) params.set('assigned', assigned);
  if (q) params.set('q', q);

  const list = useQuery({
    queryKey: ['incidents', activeTenantId, params.toString()],
    enabled: !!activeTenantId && enabled,
    queryFn: () => api<IncidentRow[]>(`${base}?${params.toString()}`),
    refetchInterval: 30_000,
  });

  // Demo history (two months of tagged incidents and requests for a customer, no emails): Super Admin only.
  const [demoDataOpen, setDemoDataOpen] = useState(false);
  const demoStatus = useQuery({
    queryKey: ['demo-data', activeTenantId],
    enabled: !!activeTenantId && enabled && can('intake:manage'),
    queryFn: () => api<{ incidents: number; requests: number; problems: number }>(`${base}/demo-data`),
  });
  const hasDemoData = (demoStatus.data?.incidents ?? 0) + (demoStatus.data?.requests ?? 0) > 0;
  const demoData = useMutation({
    mutationFn: (remove: boolean) => api<Record<string, number>>(`${base}/demo-data`, { method: remove ? 'DELETE' : 'POST' }),
    onSuccess: (r, remove) => {
      qc.invalidateQueries();
      setDemoDataOpen(false);
      notify(remove ? 'Demo data removed' : 'Demo history added', { body: remove ? `${r.incidents} incidents and ${r.requests} requests taken away.` : `${r.incidents} incidents, ${r.requests} requests and ${r.problems} problems added.` });
    },
    onError: (e) => notify('Could not do that', { intent: 'error', body: e instanceof Error ? e.message : undefined }),
  });

  const open = (id: string) => navigate(`/incidents/${id}`);

  const filtered = (list.data ?? []).filter(incQueueFilter(staff && status === 'open' ? tile : 'all'));
  const { sorted: rows, header } = useSort(filtered, {
    ref: (r) => r.reference,
    title: (r) => r.title,
    category: (r) => INC_CATEGORY_BY_KEY[r.category]?.label ?? r.category,
    site: (r) => r.sitecode,
    priority: (r) => r.priority,
    status: (r) => INC_STATUS_LABELS[r.status],
    assigned: (r) => r.assigned_agent_name ?? r.assigned_to_name,
    raised: (r) => r.created_at,
  });

  if (!activeTenantId) return <NoTenant />;
  if (!enabled) {
    return (
      <Page title="Incidents" subtitle="Report and track problems with your Teams calling.">
        <Card>
          <Text>
            Managed Services isn&apos;t switched on for this customer. A Super Admin can switch it on from <b>Customers</b>.
          </Text>
        </Card>
      </Page>
    );
  }

  const statusLabel = staff ? INC_STATUS_LABELS : INC_STATUS_CUSTOMER_LABELS;

  return (
    <Page
      title="Incidents"
      subtitle="Something not working, or not working well? Raise an incident and follow it here. You're emailed as it moves on."
      actions={
        <div className={cs.actions}>
          {can('inc:create') && (
            <Button appearance="primary" onClick={() => navigate('/incidents/new')}>
              Raise an incident
            </Button>
          )}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button icon={<MoreHorizontalRegular />} aria-label="More incident tools">
                More
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setReportsOpen(true)}>Reports</MenuItem>
                <MenuItem onClick={() => setNoticesOpen(true)}>Past notices</MenuItem>
                {staff && <MenuItem onClick={() => navigate('/incidents/problems')}>Problems</MenuItem>}
                {staff && <MenuItem onClick={() => setKnowledgeOpen(true)}>Knowledge</MenuItem>}
                <MenuItem onClick={() => setSettingsOpen(true)}>{staff ? 'Settings' : 'Our targets'}</MenuItem>
                {can('intake:manage') && (
                  <MenuItem onClick={() => setDemoDataOpen(true)}>{hasDemoData ? 'Remove demo data…' : 'Add demo history…'}</MenuItem>
                )}
                {can('intake:manage') && sandbox && (
                  <MenuItem
                    disabled={demo.isPending}
                    onClick={() => window.confirm('Add six demo incidents to this test customer? Their emails go to the test address.') && demo.mutate()}
                  >
                    Add demo incidents
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      }
    >
      <div className={cs.toolbar}>
        <Field label="Show">
          <Select value={status} onChange={(_, d) => setStatus(d.value)}>
            <option value="open">Not closed yet</option>
            {INC_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel[s]}
              </option>
            ))}
            <option value="all">All</option>
          </Select>
        </Field>
        <Field label="Category">
          <Select value={category} onChange={(_, d) => setCategory(d.value)}>
            <option value="">All categories</option>
            {INC_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Priority">
          <Select value={priority} onChange={(_, d) => setPriority(d.value)}>
            <option value="">Any priority</option>
            {INC_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {INC_PRIORITY_LABELS[p]}
              </option>
            ))}
          </Select>
        </Field>
        {staff && (
          <Field label="Assigned">
            <Select value={assigned} onChange={(_, d) => setAssigned(d.value)}>
              <option value="">Anyone</option>
              <option value="me">Assigned to me</option>
              <option value="none">Unassigned</option>
              <option value="agent">With an assistant (AI)</option>
            </Select>
          </Field>
        )}
        <Field label="Search">
          <Input value={qInput} placeholder="Title or INC-0042" onChange={(_, d) => setQInput(d.value)} />
        </Field>
      </div>
      <IncidentNotices base={base} enabled={enabled} />
      {list.isError && <LoadError message={(list.error as Error).message} />}
      {staff && status === 'open' && list.data && <IncQueueTiles items={list.data} filter={tile} onFilter={setTile} />}
      <Card>
        {list.isLoading ? (
          <TableSkeleton rows={5} cols={6} label="Loading incidents" />
        ) : rows.length === 0 ? (
          list.data && list.data.length > 0 ? (
            <EmptyState title="Nothing matches that filter" hint="Clear a filter or search for something else." />
          ) : status === 'open' ? (
            <EmptyState
              title="No open incidents"
              hint="Nothing is being worked on right now."
              action={
                can('inc:create') && (
                  <Button size="small" appearance="primary" onClick={() => navigate('/incidents/new')}>
                    Raise an incident
                  </Button>
                )
              }
            />
          ) : (
            <EmptyState title="No incidents here" />
          )
        ) : (
          <DataTable size="small" minWidth={980}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell {...header('ref')}>Ref</TableHeaderCell>
                <TableHeaderCell {...header('title')}>Title</TableHeaderCell>
                <TableHeaderCell {...header('category')}>Category</TableHeaderCell>
                <TableHeaderCell {...header('site')}>Site</TableHeaderCell>
                <TableHeaderCell {...header('priority')}>Priority</TableHeaderCell>
                <TableHeaderCell {...header('status')}>Status</TableHeaderCell>
                {staff && <TableHeaderCell>Next target</TableHeaderCell>}
                <TableHeaderCell {...header('assigned')}>Assigned to</TableHeaderCell>
                <TableHeaderCell {...header('raised')}>Raised</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className={cs.row} onClick={() => open(r.id)}>
                  <TableCell>
                    <Link onClick={() => open(r.id)}>{r.reference}</Link>
                  </TableCell>
                  <TableCell>
                    {r.title}
                    <MajorBadge row={r} />
                  </TableCell>
                  <TableCell>{INC_CATEGORY_BY_KEY[r.category]?.label ?? r.category}</TableCell>
                  <TableCell>{r.sitecode ?? '—'}</TableCell>
                  <TableCell>
                    <PriorityBadge priority={r.priority} staff={staff} />
                  </TableCell>
                  <TableCell>
                    <IncStatusBadge status={r.status} hold={r.hold_reason} staff={staff} />
                  </TableCell>
                  {staff && (
                    <TableCell>
                      <IncSlaBadge clock={r.sla.clock} which={r.sla.which} />
                      {r.major && <MajorClockBadge clock={r.major_clock} />}
                    </TableCell>
                  )}
                  <TableCell>{r.assigned_agent_name ? `${r.assigned_agent_name} (AI)` : (r.assigned_to_name ?? (r.status === 'new' ? 'Not yet' : '—'))}</TableCell>
                  <TableCell>
                    {r.reporter_name ?? '—'}
                    <Text size={100} block>
                      {new Date(r.created_at).toLocaleString()}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </Card>
      {demoDataOpen && (
        <Dialog open onOpenChange={(_, d) => !d.open && setDemoDataOpen(false)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{hasDemoData ? 'Remove the demo data?' : 'Add demo history?'}</DialogTitle>
              <DialogContent>
                {hasDemoData ? (
                  <Text>
                    This takes away the {demoStatus.data?.incidents} demo incidents, {demoStatus.data?.requests} demo requests and {demoStatus.data?.problems} demo problems (and the knowledge saved from them). Anything real stays.
                  </Text>
                ) : (
                  <Text>
                    Adds about two months of believable history to <b>this customer</b>: around 45 incidents (most closed, some open, on hold or past their target), a live and two past major incidents with reviews, problems, knowledge, and 27 service requests at every stage. The people in it come from this customer&apos;s Discovery. Nothing is emailed, and everything is tagged so you can remove it in one click. Other people who work on this customer will see it until it is removed.
                  </Text>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setDemoDataOpen(false)}>
                  Cancel
                </Button>
                <Button appearance="primary" disabled={demoData.isPending} onClick={() => demoData.mutate(hasDemoData)}>
                  {demoData.isPending ? 'Working…' : hasDemoData ? 'Remove demo data' : 'Add demo history'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
      {noticesOpen && <PastNoticesDialog base={base} enabled={enabled} onClose={() => setNoticesOpen(false)} />}
      {knowledgeOpen && <KnowledgeDialog base={base} onClose={() => setKnowledgeOpen(false)} />}
      {settingsOpen && <IncidentSettingsDialog base={base} onClose={() => setSettingsOpen(false)} />}
      {reportsOpen && <IncidentReportsDialog base={base} onClose={() => setReportsOpen(false)} />}
    </Page>
  );
}
